// Click-to-edit one sentence: which unit of the note's Markdown
// (lib/noteSentences) a click on the rendered note landed on, and the DOM
// range that shows it. The reading view renders the Markdown, so its blocks
// are matched to the Markdown lines by their text (spaces ignored, the
// literal "•" / "1." list markers the renderer adds left out), in order.
// Returns null when the click cannot be placed; the caller then opens the
// full editor as before.

import {
  lineUnits,
  type NoteLine,
  type NoteUnit,
  noteLines,
  plainText,
  unitText,
} from "./noteSentences";

const BLOCKS = "p, li, h1, h2, h3, h4, h5, h6";
// Not note text: "verify" chips and controls. Highlighted values (<mark>)
// are the note's own words.
const NOT_TEXT = "[data-verify-index]:not(mark), button, [aria-hidden=true]";
const SPACE = /\s/u;
const BULLET_GLYPH = /[•·◦▪‣]/u;
// Only controls inside `within`: a modal sheet sets aria-hidden on the whole
// page around it (the source sheet on phones), which is not a control.
const skipped = (element: Element, within: Element): boolean => {
  const control = element.closest(NOT_TEXT);
  return !!control && within.contains(control);
};

interface Char {
  node: Text;
  offset: number;
}

interface Block {
  element: Element;
  chars: Char[];
  text: string;
}

const readBlock = (element: Element): Block => {
  let chars: Char[] = [];
  const walker = element.ownerDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
  );
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    const parent = text.parentElement;
    if (!parent || parent.closest(BLOCKS) !== element) continue;
    if (skipped(parent, element)) continue;
    for (let offset = 0; offset < text.data.length; offset += 1)
      if (!SPACE.test(text.data[offset] ?? ""))
        chars.push({ node: text, offset });
  }
  const raw = chars.map((char) => char.node.data[char.offset]).join("");
  // The renderer writes list markers as text ("• ", "2. ").
  const inList = element.tagName === "LI" || !!element.closest("li");
  const marker = inList ? /^(?:•|\d{1,3}\.)/u.exec(raw) : null;
  if (marker) chars = chars.slice(marker[0].length);
  return {
    element,
    chars,
    text: chars.map((char) => char.node.data[char.offset]).join(""),
  };
};

interface Line {
  line: NoteLine;
  // The line's shown text without spaces, and each character's index in
  // the plain text; then the plain text's source map.
  text: string;
  toPlain: number[];
  map: number[];
}

const readLines = (markdown: string): Line[] =>
  noteLines(markdown)
    .filter((line) => line.kind !== "blank" && line.kind !== "skip")
    .map((line) => {
      const { plain, map } = plainText(markdown, line.contentStart, line.end);
      let text = "";
      const toPlain: number[] = [];
      for (let index = 0; index < plain.length; index += 1) {
        const char = plain[index] ?? "";
        if (SPACE.test(char)) continue;
        text += char;
        toPlain.push(index);
      }
      return { line, text, toPlain, map };
    })
    .filter((line) => line.text.length > 0);

interface Placed {
  line: Line;
  block: number;
  // Where the line's text starts in the block's text.
  at: number;
}

// Each Markdown line placed in the block that shows it, in reading order.
// A line that matches nowhere (rendered differently) is left out.
const placeLines = (lines: Line[], blocks: Block[]): Placed[] => {
  const placed: Placed[] = [];
  let block = 0;
  let cursor = 0;
  for (const line of lines) {
    for (let k = block; k < Math.min(blocks.length, block + 4); k += 1) {
      let from = k === block ? cursor : 0;
      const text = blocks[k]?.text ?? "";
      // A bullet written as text ("• Advised fluids", as generated notes
      // write them) renders inside a paragraph with its glyph; the line's
      // text starts after it.
      if (
        BULLET_GLYPH.test(text[from] ?? "") &&
        !BULLET_GLYPH.test(line.text[0] ?? "")
      )
        from += 1;
      if (!text.startsWith(line.text, from)) continue;
      placed.push({ line, block: k, at: from });
      block = k;
      cursor = from + line.text.length;
      if (cursor >= (blocks[k]?.text.length ?? 0)) {
        block += 1;
        cursor = 0;
      }
      break;
    }
  }
  return placed;
};

export interface SentenceHit {
  unit: NoteUnit;
  // What the editor shows (plain text).
  text: string;
  // The unit in the rendered note (for the highlight and placement).
  range: Range | null;
}

// The unit under a caret position in the rendered note `root`.
export const sentenceAtCaret = (
  root: HTMLElement,
  markdown: string,
  caret: { node: Node; offset: number },
): SentenceHit | null => {
  if (!root.contains(caret.node)) return null;
  const start =
    caret.node instanceof Element ? caret.node : caret.node.parentElement;
  if (!start || skipped(start, root)) return null;
  const clicked = start.closest(BLOCKS);
  if (!clicked || !root.contains(clicked)) return null;
  const blocks = [...root.querySelectorAll(BLOCKS)].map(readBlock);
  const index = blocks.findIndex((block) => block.element === clicked);
  const block = blocks[index];
  if (!block?.chars.length) return null;

  // Characters of the block before the caret.
  const doc = root.ownerDocument;
  const point = doc.createRange();
  point.setStart(caret.node, caret.offset);
  point.collapse(true);
  let offset = 0;
  for (const char of block.chars) {
    if (point.comparePoint(char.node, char.offset) >= 0) break;
    offset += 1;
  }

  const placed = placeLines(readLines(markdown), blocks).filter(
    (item) => item.block === index,
  );
  if (!placed.length) return null;
  const position = Math.min(offset, block.text.length - 1);
  const hit =
    placed.find(
      (item) =>
        item.at <= position && position < item.at + item.line.text.length,
    ) ??
    [...placed].reverse().find((item) => item.at <= position) ??
    placed[0];
  if (!hit) return null;
  const { line, at } = hit;
  const within = Math.max(0, Math.min(position - at, line.text.length - 1));
  const plainIndex = line.toPlain[within] ?? 0;
  const source = line.map[plainIndex] ?? line.line.contentStart;
  const units = lineUnits(markdown, line.line);
  const unit =
    units.find((item) => item.start <= source && source < item.end) ??
    [...units].reverse().find((item) => item.start <= source) ??
    units[0];
  if (!unit) return null;

  // The unit's characters in the block, for its range.
  let first = -1;
  let last = -1;
  line.toPlain.forEach((plain, ws) => {
    const at = line.map[plain] ?? -1;
    if (at < unit.start || at >= unit.end) return;
    if (first === -1) first = ws;
    last = ws;
  });
  let range: Range | null = null;
  const from = block.chars[hit.at + first];
  const to = block.chars[hit.at + last];
  if (first !== -1 && from && to) {
    range = doc.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset + 1);
  }
  return { unit, text: unitText(markdown, unit), range };
};

type CaretDocument = Document & {
  caretPositionFromPoint?: (
    x: number,
    y: number,
  ) => { offsetNode: Node; offset: number } | null;
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
};

// The caret position under a point, or null (no browser support, or not on
// text).
export const caretAtPoint = (
  doc: Document,
  x: number,
  y: number,
): { node: Node; offset: number } | null => {
  const caretDoc = doc as CaretDocument;
  try {
    if (typeof caretDoc.caretPositionFromPoint === "function") {
      const position = caretDoc.caretPositionFromPoint(x, y);
      return position
        ? { node: position.offsetNode, offset: position.offset }
        : null;
    }
    if (typeof caretDoc.caretRangeFromPoint === "function") {
      const range = caretDoc.caretRangeFromPoint(x, y);
      return range
        ? { node: range.startContainer, offset: range.startOffset }
        : null;
    }
  } catch {
    return null;
  }
  return null;
};
